import { useCallback } from 'react';

import { GOOGLE_RECAPTCHA_SITE_KEY } from '~/constants';
import { delay, log } from '~/util';

export const RECAPTCHA_CONTAINER_ID = 'google-recaptcha-container';

// Strings must stay in sync with pkg/recaptcha/actions.go.
export enum RecaptchaAction {
  Login = 'login',
  MagicLink = 'magic_link',
  Register = 'register',
  PasswordResetRequest = 'password_reset_request',
  PasswordReset = 'password_reset',
  MfaSetup = 'mfa_setup',
  UpdateUser = 'update_user',
  Contact = 'contact',
}

const BADGE_ACTION = 'badge';
const RECAPTCHA_LOAD_TIMEOUT_MS = 10_000;
const RECAPTCHA_LOAD_POLL_INTERVAL_MS = 50;
const RECAPTCHA_SCRIPT_PATH = '/recaptcha/enterprise.js';

// Stages an attempt passes through. A failure reports the last one reached, which is what separates a
// script that never arrived from a token the browser could not mint.
export const RecaptchaStage = {
  ScriptLoaded: 'recaptcha_script_loaded',
  Ready: 'recaptcha_ready',
  ExecuteStarted: 'recaptcha_execute_started',
  ExecuteResolved: 'recaptcha_execute_resolved',
  ExecuteRejected: 'recaptcha_execute_rejected',
} as const;

type StageTrace = {
  // markOnce keeps the first timing, for the page-lifecycle stages an attempt may observe twice.
  markOnce: (stage: string) => void;
  mark: (stage: string) => void;
  context: () => Record<string, unknown>;
};

const newStageTrace = (action: string): StageTrace => {
  const startedAt = performance.now();
  const elapsed = () => Math.round(performance.now() - startedAt);
  const attempt_id = `${Math.round(startedAt)}-${Math.random().toString(36).slice(2, 8)}`;
  const stage_ms: Record<string, number> = {};
  let stage: string | undefined;
  let executions = 0;

  const mark = (reached: string) => {
    stage = reached;
    stage_ms[reached] = elapsed();
  };

  return {
    mark: (reached: string) => {
      if (reached === RecaptchaStage.ExecuteStarted) {
        executions += 1;
      }
      mark(reached);
    },
    markOnce: (reached: string) => {
      if (!(reached in stage_ms)) {
        mark(reached);
      }
    },
    context: () => ({
      action,
      attempt_id,
      stage,
      stage_ms,
      executions,
      duration_ms: elapsed(),
      script_hosts: recaptchaScriptHosts(),
    }),
  };
};

// Which host actually served the script. Two hosts means the google.com tag failed and the
// recaptcha.net fallback in index.html took over.
const recaptchaScriptHosts = (): string[] =>
  Array.from(document.querySelectorAll<HTMLScriptElement>(`script[src*="${RECAPTCHA_SCRIPT_PATH}"]`)).map(
    (script) => new URL(script.src, window.location.href).host
  );

// An Error does not survive JSON, so the message has to be lifted out before it reaches the logger.
export const describeError = (error: unknown): Record<string, string> => {
  if (error instanceof Error) {
    return { error_name: error.name, error_message: error.message };
  }
  return { error_name: typeof error, error_message: String(error) };
};

type ReCaptchaRenderParameters = {
  sitekey: string;
  action: string;
  badge: 'inline' | 'bottomleft' | 'bottomright';
  size: 'invisible';
};

type ReCaptchaExecute = {
  (widgetId: number): Promise<string>;
  (siteKey: string, options: { action: string }): Promise<string>;
};

type ReCaptchaEnterprise = {
  ready: (callback: () => void) => void;
  render: (container: string | HTMLElement, parameters: ReCaptchaRenderParameters) => number;
  execute: ReCaptchaExecute;
};

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: ReCaptchaEnterprise;
    };
  }
}

let badgeRendered = false;

export const getReCaptchaEnterprise = async (trace?: StageTrace): Promise<ReCaptchaEnterprise> => {
  return new Promise<ReCaptchaEnterprise>((resolve, reject) => {
    let readyPending = false;

    const cleanup = () => {
      window.clearTimeout(timeout);
      window.clearInterval(poll);
      document.removeEventListener('load', handleScriptLoad, true);
    };
    const resolveIfAvailable = () => {
      if (readyPending) {
        return;
      }

      const enterprise = window.grecaptcha?.enterprise;
      if (!enterprise) {
        return;
      }

      readyPending = true;
      trace?.markOnce(RecaptchaStage.ScriptLoaded);
      enterprise.ready(() => {
        trace?.markOnce(RecaptchaStage.Ready);
        cleanup();
        resolve(enterprise);
      });
    };
    const handleScriptLoad = (event: Event) => {
      if (
        event.target instanceof HTMLScriptElement &&
        new URL(event.target.src, window.location.href).pathname.endsWith(RECAPTCHA_SCRIPT_PATH)
      ) {
        resolveIfAvailable();
      }
    };
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error('reCaptcha load timed out'));
    }, RECAPTCHA_LOAD_TIMEOUT_MS);
    const poll = window.setInterval(resolveIfAvailable, RECAPTCHA_LOAD_POLL_INTERVAL_MS);

    document.addEventListener('load', handleScriptLoad, true);
    resolveIfAvailable();
  });
};

const ensureBadgeRendered = async (siteKey: string, trace?: StageTrace): Promise<void> => {
  if (badgeRendered) {
    return;
  }

  if (!siteKey) {
    throw new Error('reCaptcha site key not configured');
  }

  const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    throw new Error('reCaptcha container not found');
  }

  const enterprise = await getReCaptchaEnterprise(trace);

  enterprise.render(container, {
    sitekey: siteKey,
    action: BADGE_ACTION,
    badge: 'inline',
    size: 'invisible',
  });

  badgeRendered = true;
};

// A failed mint resolves with a token rather than rejecting, and the server rejects it as BROWSER_ERROR.
// Google documents no format, but every such token observed in prod starts "HF" and every accepted one
// starts "0c". Only "HF" retries, so a format change costs the retry rather than retrying every mint.
const MINT_ERROR_TOKEN_PREFIX = 'HF';
const MINT_ATTEMPTS = 3;

const MINT_RETRY_BASE_DELAY_MS = 500;

// Exported so a caller can report that it sent a token the server will reject anyway.
export const isMintErrorToken = (token: string): boolean => token.startsWith(MINT_ERROR_TOKEN_PREFIX);

// Failures are independent, so retrying clears most of them. Exported for tests.
export const mintWithRetry = async (mint: () => Promise<string>, action: string): Promise<string> => {
  let token = await mint();
  for (let attempt = 1; attempt < MINT_ATTEMPTS && isMintErrorToken(token); attempt++) {
    log({ name: 'reCaptcha mint retried', type: 'warn', context: { action, attempt } });
    await delay(MINT_RETRY_BASE_DELAY_MS * attempt);
    token = await mint();
  }

  return token;
};

const executeReCaptcha = async (siteKey: string, action: RecaptchaAction, trace: StageTrace): Promise<string> => {
  await ensureBadgeRendered(siteKey, trace);
  const enterprise = await getReCaptchaEnterprise(trace);

  return mintWithRetry(async () => {
    trace.mark(RecaptchaStage.ExecuteStarted);
    try {
      const token = await enterprise.execute(siteKey, { action });
      trace.mark(RecaptchaStage.ExecuteResolved);
      return token;
    } catch (error) {
      trace.mark(RecaptchaStage.ExecuteRejected);
      throw error;
    }
  }, action);
};

type ReCaptchaConfig = { optional?: boolean };

// optional: true resolves to undefined instead of rejecting, for endpoints the server assesses but does
// not enforce, so a network that breaks reCAPTCHA cannot block the action.
type ReCaptchaExecutor = {
  (action: RecaptchaAction, config: { optional: true }): Promise<string | undefined>;
  (action: RecaptchaAction, config?: { optional?: false }): Promise<string>;
};

// Exported for tests: the hook only binds the site key to this.
export const runReCaptchaAttempt = (
  siteKey: string,
  action: RecaptchaAction,
  config?: ReCaptchaConfig
): Promise<string | undefined> => {
  const trace = newStageTrace(action);

  return executeReCaptcha(siteKey, action, trace)
    .then((token) => {
      // A token still error-shaped after every retry is a failure the server has yet to reject.
      const errorShaped = isMintErrorToken(token);
      log({
        name: 'reCaptcha attempt',
        type: errorShaped ? 'warn' : 'info',
        context: { ...trace.context(), minted: true, token_error_shaped: errorShaped },
      });
      return token;
    })
    .catch((error: unknown) => {
      const described = describeError(error);
      log({
        name: 'reCaptcha attempt failed',
        type: 'error',
        context: { ...trace.context(), ...described, minted: false, optional: config?.optional === true },
      });
      if (config?.optional) {
        return undefined;
      }
      if (described.error_message === 'Challenge cancelled by user.') {
        throw new Error('reCAPTCHA challenge not completed');
      }
      throw new Error('reCAPTCHA error');
    });
};

export const useReCAPTCHA = (): ReCaptchaExecutor => {
  return useCallback(
    ((action: RecaptchaAction, config?: ReCaptchaConfig) =>
      runReCaptchaAttempt(GOOGLE_RECAPTCHA_SITE_KEY, action, config)) as ReCaptchaExecutor,
    [GOOGLE_RECAPTCHA_SITE_KEY]
  );
};
